import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { AssetStore } from '../../stores/asset.store';
import { AssetType } from '../../types/asset-type.interface';
import { EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

interface AssetTypeFormAsset {
  readonly assetTypeId: number;
}

@Component({
  selector: 'app-asset-type-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    GenericEntityFormComponent,
    TooltipModule,
  ],
  templateUrl: './asset-type-form.component.html',
})
export class AssetTypeFormComponent implements OnInit, OnChanges {
  @Input() entityDefinition?: EntityDefinition<AssetType>;
  @Input() entity?: AssetType;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<AssetType>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<AssetType>();

  private readonly assetStore = inject(AssetStore);
  private readonly config = inject<DynamicDialogConfig<EntityDialogData<AssetType>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<AssetTypeFormComponent> | null>(DynamicDialogRef, {
    optional: true,
  });

  private directDeleteCallback?: (entity: AssetType) => void;

  // Mirrors the entity @Input into a signal so assetCountByType reacts to it: the input is also
  // reassigned imperatively from DynamicDialogConfig data in ngOnInit, outside Angular's normal
  // change-detection binding path, so ngOnChanges alone wouldn't catch that source.
  private readonly _entity = signal<AssetType | undefined>(undefined);

  protected readonly assetCountByType = computed(() => {
    const result: Record<number, number> = {};
    const store = this.assetStore as unknown as { entities(): readonly AssetTypeFormAsset[] };
    const assets = store.entities();
    const entity = this._entity();

    if (entity) {
      result[entity.id] = 0;
    }

    const entityId = entity?.id;
    if (entityId !== undefined) {
      assets.forEach(asset => {
        if (asset.assetTypeId === entityId) {
          result[entityId] = (result[entityId] ?? 0) + 1;
        }
      });
    }

    return result;
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entity']) {
      this._entity.set(this.entity);
    }
  }

  ngOnInit(): void {
    const data = this.config?.data;
    if (data) {
      this.entityDefinition = data.entityDefinition;
      this.mode = data.mode === 'edit' ? 'edit' : 'create';
      if (data.entity !== undefined) this.entity = data.entity;
      if (data.parentId !== undefined) this.parentId = data.parentId;
      if (data.additionalData !== undefined) this.additionalData = data.additionalData;
      if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
    }
    this._entity.set(this.entity);

    if (this.mode === 'edit' && this.entity) {
      const store = this.assetStore as unknown as { loadAll(): void };
      store.loadAll();
    }
  }

  onFormSubmit(formData: EntityFormValue<AssetType>): void {
    if (this.dialogRef) {
      this.dialogRef.close(formData);
    } else {
      this.formSubmit.emit(formData);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDeleteRequest(entity: AssetType): void {
    if (this.directDeleteCallback) {
      this.directDeleteCallback(entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<AssetType> = { __action: 'delete', entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(entity);
    }
  }

  getAssetCount(typeId: number): number {
    return this.assetCountByType()[typeId] ?? 0;
  }
}
